import React from 'react';
import { Col, Row, Table, Typography } from 'antd';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import { MULTI_DEPT_BUDGET_ORG_CODE } from './PrepaymentForm';
import {
  prepaymentAmountLabel,
  prepaymentLineSectionTitle,
  prepaymentTotalLabel,
  resolvePrepaymentBizWord
} from '../../utils/prepaymentDocTypeLabels';
import './ExpenseClaimForm.css';

const { Text } = Typography;
const COL3 = 8;
const GUTTER = [16, 8];

export function findOrgDisplayName(organizations, code, fallbackName) {
  const orgCode = String(code || '').trim();
  const name = String(fallbackName || '').trim();
  if (name && (!orgCode || name !== orgCode)) return name;
  if (!orgCode) return '—';
  const hit = (organizations || []).find((o) => o.org_code === orgCode);
  return hit?.org_name || name || orgCode;
}

export function resolveApplicantDeptDisplay(formData = {}, organizations = []) {
  return findOrgDisplayName(
    organizations,
    formData.applicant_dept_code,
    formData.applicant_dept_name
  );
}

function ReadonlyItem({ label, value, span = COL3 }) {
  return (
    <Col xs={24} md={span}>
      <div className="prepayment-readonly-item">
        <Text type="secondary" className="prepayment-readonly-label">{label}</Text>
        <div className="prepayment-readonly-value">{value ?? '—'}</div>
      </div>
    </Col>
  );
}

export function PrepaymentBasicInfoReadonly({
  prepayment,
  formData = {},
  organizations = [],
  sectionSuffix = '（原预付款）',
  applicantPrepaymentSummary
}) {
  const urgency = formData.urgency_label || (formData.urgency === 'urgent' ? '紧急' : '正常');
  const suffix = sectionSuffix ? sectionSuffix : '';
  const summaryLabel = resolvePrepaymentBizWord(formData.doc_type);
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{`基本信息${suffix}`}</div>
      <Row gutter={GUTTER} className="prepayment-readonly-grid">
        <ReadonlyItem label="申请人" value={prepayment?.applicant_name || formData.applicant_name} />
        <ReadonlyItem label="申请人部门" value={resolveApplicantDeptDisplay(formData, organizations)} />
        <ReadonlyItem label="费用承担公司" value={formData.expense_company_name} />
        <ReadonlyItem label="单据类型" value={formData.doc_type || '预付款申请'} />
        <ReadonlyItem label="紧急程度" value={urgency} />
        <ReadonlyItem label="预算一级科目" value={formData.category_name || formData.budget_subject_label} />
        <ReadonlyItem label="费用产生时间" value={formData.expense_occurrence_month} />
        <ReadonlyItem label="所属预算周期" value={formData.budget_period_label} />
        <ReadonlyItem label="事由" value={formData.reason} span={24} />
        <ReadonlyItem label="主题" value={formData.subject} span={24} />
        {applicantPrepaymentSummary && (
          <Col xs={24}>
            <div className="prepayment-applicant-summary-hint">
              该申请人已通过{summaryLabel}累计 {formatMoney(applicantPrepaymentSummary.approved_total)}，
              剩余未核销合计 {formatMoney(applicantPrepaymentSummary.remaining_total)}
            </div>
          </Col>
        )}
      </Row>
    </section>
  );
}

const PAYEE_TYPE_LABEL = { employee: '员工', external_person: '外部个人', supplier: '供应商' };

function hasReadonlyValue(value) {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== '' && text !== '—';
}

function resolvePayeeTypeKey(formData = {}) {
  const type = String(formData.payee_type || '').trim();
  if (type) return type;
  const label = String(formData.payee_type_label || '').trim();
  if (label.includes('员工')) return 'employee';
  if (label.includes('外部')) return 'external_person';
  if (label.includes('供应商')) return 'supplier';
  return '';
}

export function buildPayeeReadonlyFields(formData = {}, options = {}) {
  const { context = 'prepayment' } = options;
  const payeeTypeKey = resolvePayeeTypeKey(formData);
  const payeeTypeLabel = formData.payee_type_label
    || PAYEE_TYPE_LABEL[payeeTypeKey]
    || formData.payee_type;
  const fields = [
    { label: '收款方类型', value: payeeTypeLabel },
    { label: '收款方名称', value: formData.payee_name },
    { label: '开户银行', value: formData.bank_name },
    { label: '支行名称', value: formData.bank_branch_name, hideFor: ['employee'] },
    { label: '银行账号', value: formData.bank_account_no },
    { label: '收款人身份证号', value: formData.payee_id_card_no, showFor: ['external_person'] },
    { label: '对方税号', value: formData.payee_tax_no, showFor: ['supplier'] },
    { label: '预计收票日期', value: formData.expected_invoice_date, prepaymentOnly: true }
  ];
  return fields.filter((field) => {
    if (field.prepaymentOnly && context !== 'prepayment') return false;
    if (field.hideFor?.includes(payeeTypeKey)) return false;
    if (field.showFor && !field.showFor.includes(payeeTypeKey)) return false;
    return hasReadonlyValue(field.value);
  });
}

export function PrepaymentPayeeReadonly({
  formData = {},
  sectionSuffix = '（原预付款）',
  commitmentFiles,
  context = 'prepayment'
}) {
  const files = commitmentFiles ?? formData.no_invoice_commitment_files ?? [];
  const suffix = sectionSuffix ? sectionSuffix : '';
  const payeeFields = buildPayeeReadonlyFields(formData, { context });
  if (!payeeFields.length && !files.length) return null;
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{`收款方${suffix}`}</div>
      <Row gutter={GUTTER} className="prepayment-readonly-grid">
        {payeeFields.map((field) => (
          <ReadonlyItem key={field.label} label={field.label} value={field.value} />
        ))}
        {files.length > 0 && (
          <Col xs={24}>
            <div className="prepayment-readonly-item">
              <Text type="secondary" className="prepayment-readonly-label">无票承诺书</Text>
              <SealApprovalAttachmentsView attachments={files} title="" />
            </div>
          </Col>
        )}
      </Row>
    </section>
  );
}

function formatMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `¥${n.toFixed(2)}` : '—';
}

export function buildPrepaymentLineRows(lines = [], organizations = []) {
  const rows = [];
  (lines || []).forEach((line, index) => {
    const isMulti = String(line?.budget_org_code || '').trim() === MULTI_DEPT_BUDGET_ORG_CODE;
    if (isMulti && Array.isArray(line.budget_splits) && line.budget_splits.length) {
      line.budget_splits.forEach((split, splitIndex) => {
        rows.push({
          key: `${index}-${splitIndex}`,
          lineNo: index + 1,
          budget_org_name: findOrgDisplayName(organizations, split.org_code, split.org_name),
          subitem_name: line.subitem_name || '—',
          expense_detail: line.expense_detail || '—',
          prepay_amount: split.amount
        });
      });
      return;
    }
    rows.push({
      key: String(index),
      lineNo: index + 1,
      budget_org_name: findOrgDisplayName(
        organizations,
        line.budget_org_code || line.cost_org_code,
        line.budget_org_name
      ),
      subitem_name: line.subitem_name || '—',
      expense_detail: line.expense_detail || '—',
      prepay_amount: line.prepay_amount
    });
  });
  return rows;
}

export function PrepaymentLinesReadonly({
  prepayment,
  formData = {},
  organizations = [],
  variant = 'writeoff',
  sectionSuffix = '（原预付款）'
}) {
  const remaining = Number(prepayment?.remaining_amount || 0);
  const rows = buildPrepaymentLineRows(formData.lines, organizations);
  const suffix = sectionSuffix ? sectionSuffix : '';
  const bizWord = resolvePrepaymentBizWord(formData.doc_type);
  const columns = [
    { title: '序号', dataIndex: 'lineNo', width: 56 },
    { title: '费用归属部门', dataIndex: 'budget_org_name', width: 140, ellipsis: true },
    { title: '预算二级科目', dataIndex: 'subitem_name', width: 120, ellipsis: true },
    { title: '费用明细', dataIndex: 'expense_detail', width: 88, ellipsis: true },
    {
      title: prepaymentAmountLabel(formData.doc_type),
      dataIndex: 'prepay_amount',
      width: 110,
      align: 'right',
      render: (v) => formatMoney(v)
    }
  ];

  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">
        {variant === 'application'
          ? prepaymentLineSectionTitle(formData.doc_type)
          : `原${bizWord}明细${suffix}`}
      </div>
      {variant === 'writeoff' && (
        <Row gutter={GUTTER} className="prepayment-readonly-grid" style={{ marginBottom: 12 }}>
          <ReadonlyItem label="预付金额" value={formatMoney(prepayment?.prepaid_amount)} />
          <ReadonlyItem label="已核销金额" value={formatMoney(prepayment?.written_off_amount)} />
          <ReadonlyItem label="剩余应核销" value={formatMoney(remaining)} />
        </Row>
      )}
      {variant === 'application' && (
        <Row gutter={GUTTER} className="prepayment-readonly-grid" style={{ marginBottom: 12 }}>
          <ReadonlyItem label="预付合计" value={formatMoney(formData.prepay_total)} />
          <ReadonlyItem label="金额大写" value={formData.prepay_total_uppercase || '—'} />
          <ReadonlyItem
            label="附件张数"
            value={String(
              (formData.other_attachments || []).length
              + (formData.no_invoice_commitment_files || []).length
            )}
          />
        </Row>
      )}
      <Table
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: `暂无${prepaymentLineSectionTitle(formData.doc_type)}` }}
        scroll={{ x: 640 }}
      />
      {variant === 'writeoff' && formData.remark && (
        <Row gutter={GUTTER} className="prepayment-readonly-grid" style={{ marginTop: 12 }}>
          <ReadonlyItem label="备注" value={formData.remark} span={24} />
        </Row>
      )}
    </section>
  );
}
